import { inject, observer } from 'mobx-react';
import * as React from 'react';

import {
  Button,
  Card,
  CardContent,
  Container,
  Modal,
  ModalContent,
  ModalProvider,
  ModalTrigger,
  Notice,
  Pagination,
  Separator,
  Spinner
} from 'telia-front-react';
import JobOffersStore, { JOB_OFFERS } from '../stores/JobOffersStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import JobList, { JobListProps } from './JobList';
import GravityForm from '../forms/GravityForm';
import LanguageStore from 'src/stores/LanguageStore';

interface JobOffersProps {
  jobOffersStore?: JobOffersStore;
  languageStore?: LanguageStore;
  templateJobListProps?: JobListProps;
  offersPerPage?: number;
  offerType?: string;
  subText?: string;
}

@observer
class JobOffers extends React.Component<JobOffersProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public static defaultProps = {
    offerType: JOB_OFFERS
  };

  public componentDidMount() {
    const { jobOffersStore, offerType, offersPerPage } = this.props;
    jobOffersStore.initJobOffers(offerType, this.context.translate, offersPerPage);
  }

  public render() {
    const translate = this.context.translate;
    const { jobOffersStore, offerType, subText } = this.props;
    const jobOffersByType = jobOffersStore.jobOffersListMap.get(offerType);
    const template = { ...jobOffersByType?.listProps, type: offerType };
    return (
      <Container>
        <Card padding="medium">
          <CardContent>
            <h2>{jobOffersStore.isInternships(offerType) ? translate('workday.internship.title') : translate('workday.title')}</h2>
            {!!subText && <p>{subText}</p>}
            <Separator spacing={'small'} color={'invisible'} />
            {jobOffersByType?.isErroneous ? this.renderLoadingErrorMessage() : null}
            {jobOffersByType?.isLoading ? <Spinner /> : <JobList {...template} />}
            {jobOffersStore.isInternships(offerType) ? this.renderInternshipSuggestionForm() : null}
            {jobOffersByType?.isEmpty ? this.renderForEmptyOffers() : null}
            {jobOffersByType?.showPaginator ? this.renderPaginator() : null}
          </CardContent>
        </Card>
      </Container>
    );
  }

  private renderForEmptyOffers() {
    const { jobOffersStore, offerType } = this.props;
    return !jobOffersStore.isInternships(offerType) ? this.renderEmptyResultsetMessage() : null;
  }

  private renderInternshipSuggestionForm() {
    const { languageStore } = this.props;
    const translate = this.context.translate;
    const formName = `Praktika Telias${languageStore.currentLanguage === 'ru' ? ' rus' : ''}`;
    return (
      <>
        <Separator spacing={'xsmall'} color={'invisible'} />
        <p>{translate('workday.internship.suggestForm')}</p>
        <Separator spacing={'xsmall'} color={'invisible'} />
        <ModalProvider>
          <ModalTrigger>
            <Button text={translate('workday.internship.formButton')} type="expressive" iconRight="#arrow-right" />
          </ModalTrigger>
          <Modal size={'8-col'} hasContentComponent={true}>
            <ModalContent>
              <GravityForm name={formName} />
            </ModalContent>
          </Modal>
        </ModalProvider>
      </>
    );
  }

  private renderEmptyResultsetMessage() {
    const translate = this.context.translate;
    return (
      <>
        <Separator spacing={'xsmall'} color={'invisible'} />
        <p className={'text-bold'}>{translate('workday.empty')}</p>
      </>
    );
  }

  private renderPaginator() {
    const { jobOffersStore, offerType } = this.props;
    return (
      <>
        <Separator spacing={'xsmall'} color={'invisible'} />
        <Pagination
          pages={jobOffersStore.jobOffersListMap.get(offerType).totalPages}
          currentPage={jobOffersStore.jobOffersListMap.get(offerType).currentPage}
          onChange={(pageNumber: number) => jobOffersStore.changePage(pageNumber, offerType)}
        />
      </>
    );
  }

  private renderLoadingErrorMessage() {
    const translate = this.context.translate;
    const { jobOffersStore, offerType } = this.props;
    return (
      <>
        <Notice type={'error'}>{jobOffersStore.isInternships(offerType) ? translate('workday.internship.error') : translate('workday.error')}</Notice>
        <Separator spacing={'xsmall'} color={'invisible'} />
      </>
    );
  }
}

export default inject('jobOffersStore', 'languageStore')(JobOffers);
